import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LocationOn,
  Build,
  BuildCircle,
  Schedule,
  Person,
  Close,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface Tech {
  id: string;
  name: string;
  specialties: string[];
  availability: {
    [timeSlot: string]: boolean;
  };
}

interface Customer {
  _id: string;
  companyName: string;
  contactPerson: {
    firstName: string;
    lastName: string;
    title: string;
  };
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  customerType: string;
  status: string;
  totalValue: number;
  totalProjects: number;
  lastContactDate: string;
  assignedSalesRep: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  lastContact: string;
  notes: string;
}

interface ScheduleFormProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date;
  onScheduleCreated: (schedule: any) => void;
  prefillData?: {
    customerId?: string;
    customer?: string;
    description?: string;
    priority?: string;
    notes?: string;
    type?: string;
  };
}

const locations = ['Denver', 'Boise', 'Spokane', 'Olympia'];

const timeSlots = [
  '08:00', '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', '17:00'
];

// Mock tech data - in a real app, this would come from an API
const mockTechs: Tech[] = [
  {
    id: '1',
    name: 'Mike Rodriguez',
    specialties: ['LED Installation', 'Commercial Lighting', 'Smart Controls'],
    availability: {
      '08:00': true, '09:00': true, '10:00': false, '11:00': true, '12:00': true,
      '13:00': true, '14:00': false, '15:00': true, '16:00': true, '17:00': false
    }
  },
  {
    id: '2',
    name: 'Sarah Wilson',
    specialties: ['Residential Lighting', 'Outdoor Lighting', 'Energy Efficiency'],
    availability: {
      '08:00': false, '09:00': true, '10:00': true, '11:00': true, '12:00': false,
      '13:00': true, '14:00': true, '15:00': true, '16:00': false, '17:00': true
    }
  },
  {
    id: '3',
    name: 'David Kim',
    specialties: ['Industrial Lighting', 'Emergency Systems', 'Maintenance'],
    availability: {
      '08:00': true, '09:00': false, '10:00': true, '11:00': false, '12:00': true,
      '13:00': false, '14:00': true, '15:00': true, '16:00': true, '17:00': true
    }
  },
  {
    id: '4',
    name: 'Jennifer Lee',
    specialties: ['Service Calls', 'Repairs', 'Troubleshooting'],
    availability: {
      '08:00': true, '09:00': true, '10:00': true, '11:00': true, '12:00': true,
      '13:00': true, '14:00': true, '15:00': true, '16:00': true, '17:00': true
    }
  },
  {
    id: '5',
    name: 'Alex Chen',
    specialties: ['LED Installation', 'Smart Home Integration', 'Commercial'],
    availability: {
      '08:00': false, '09:00': true, '10:00': true, '11:00': true, '12:00': true,
      '13:00': true, '14:00': true, '15:00': false, '16:00': true, '17:00': false
    }
  }
];

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  open,
  onClose,
  selectedDate,
  onScheduleCreated,
  prefillData,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    location: '',
    type: '',
    timeSlot: '',
    customer: '',
    description: '',
    priority: 'medium',
    estimatedDuration: 4,
    notes: '',
  });
  const [selectedTechs, setSelectedTechs] = useState<string[]>([]);
  const [availableTechs, setAvailableTechs] = useState<Tech[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch customers when dialog opens
  useEffect(() => {
    if (open) {
      fetchCustomers();
      setFormData({
        location: '',
        type: prefillData?.type || '',
        timeSlot: '',
        customer: prefillData?.customer || '',
        description: prefillData?.description || '',
        priority: prefillData?.priority || 'medium',
        estimatedDuration: 4,
        notes: prefillData?.notes || '',
      });
      setSelectedTechs([]);
      setAvailableTechs([]);
      setSelectedCustomer(null);
      setError('');
    }
  }, [open, prefillData]);

  const fetchCustomers = async () => {
    try {
      const response = await mockApi.getCustomers();
      // Filter customers based on service type
      let filteredCustomers: Customer[];
      
      if (formData.type === 'service') {
        // For service calls, show only installed customers
        filteredCustomers = (response.customers as any[]).filter(
          (customer: any) => customer.status === 'Installed'
        ) as Customer[];
      } else {
        // For installations, show customers who haven't been installed yet
        filteredCustomers = (response.customers as any[]).filter(
          (customer: any) => customer.status !== 'Installed'
        ) as Customer[];
      }
      
      setCustomers(filteredCustomers);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  // Refetch customers when type changes
  useEffect(() => {
    if (formData.type) {
      fetchCustomers();
      // Clear selected customer when type changes
      setSelectedCustomer(null);
      setFormData(prev => ({
        ...prev,
        customer: '',
        description: '',
        priority: 'medium',
        notes: '',
      }));
    }
  }, [formData.type]);

  // Auto-select customer when prefillData.customerId is provided
  useEffect(() => {
    if (prefillData?.customerId && customers.length > 0) {
      const customer = customers.find(c => c._id === prefillData.customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setFormData(prev => ({
          ...prev,
          customer: customer.companyName,
          description: prefillData?.description || customer.notes || '',
          priority: prefillData?.priority || getPriorityFromStatus(customer.status),
          notes: prefillData?.notes || customer.notes || '',
        }));
      }
    }
  }, [customers, prefillData]);

  // Update available techs when location, type, or time slot changes
  useEffect(() => {
    if (formData.location && formData.type && formData.timeSlot) {
      const available = mockTechs.filter(tech => 
        tech.availability[formData.timeSlot] === true
      );
      setAvailableTechs(available);
    } else {
      setAvailableTechs([]);
    }
  }, [formData.location, formData.type, formData.timeSlot]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear dependent fields when location or type changes
    if (field === 'location' || field === 'type') {
      setFormData(prev => ({
        ...prev,
        timeSlot: '',
      }));
      setSelectedTechs([]);
    }
    
    if (field === 'timeSlot') {
      setSelectedTechs([]);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c._id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer: customer.companyName,
        description: customer.notes || '',
        priority: getPriorityFromStatus(customer.status),
        notes: customer.notes || '',
      }));
    }
  };

  const getPriorityFromStatus = (status: string): string => {
    switch (status) {
      case 'Created':
        return 'low';
      case 'Proposal':
        return 'medium';
      case 'Signed/Paid':
        return 'high';
      case 'Sold':
        return 'high';
      case 'Scheduled':
        return 'urgent';
      default:
        return 'medium';
    }
  };

  const handleTechToggle = (techId: string) => {
    setSelectedTechs(prev => 
      prev.includes(techId) 
        ? prev.filter(id => id !== techId)
        : [...prev, techId]
    );
  };

  const handleSubmit = async () => {
    if (!formData.location || !formData.type || !formData.timeSlot || !selectedCustomer) {
      setError('Please fill in all required fields');
      return;
    }

    if (selectedTechs.length === 0) {
      setError('Please select at least one technician');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const scheduleData = {
        title: `${selectedCustomer.companyName} - ${formData.type}`,
        description: formData.description,
        customer: selectedCustomer.companyName,
        customerId: selectedCustomer._id,
        scheduledDate: selectedDate.toISOString().split('T')[0],
        startTime: formData.timeSlot,
        endTime: calculateEndTime(formData.timeSlot, formData.estimatedDuration),
        estimatedDuration: formData.estimatedDuration,
        team: selectedTechs,
        assignedTo: selectedTechs[0] || '',
        status: 'scheduled',
        location: formData.location,
        region: getRegionFromLocation(formData.location),
        type: formData.type,
        priority: formData.priority,
        notes: formData.notes,
        weatherDependent: false,
        specialRequirements: [],
        recurring: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Create the schedule via API
      // Mock schedule creation
      const newSchedule = {
        id: Date.now().toString(),
        ...scheduleData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Call the callback to refresh the parent component
      onScheduleCreated(newSchedule);
      
      // Close the dialog
      onClose();
      
      // Navigate to the scheduling tab
      navigate('/scheduling');
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const calculateEndTime = (startTime: string, duration: number): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endTime = new Date();
    endTime.setHours(hours + duration, minutes);
    return endTime.toTimeString().slice(0, 5);
  };

  const getRegionFromLocation = (location: string): string => {
    const regionMap: { [key: string]: string } = {
      'Denver': 'Central',
      'Boise': 'North',
      'Spokane': 'East',
      'Olympia': 'West',
    };
    return regionMap[location] || 'Unassigned';
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1A1A1A',
          border: '1px solid #333333',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#D4AF37' }}>
            Schedule for {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Button
            onClick={onClose}
            sx={{ color: '#FFFFFF', minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Location Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#D4AF37' }}>Location *</InputLabel>
              <Select
                value={formData.location}
                label="Location *"
                onChange={(e) => handleInputChange('location', e.target.value)}
                sx={{ color: '#FFFFFF' }}
              >
                {locations.map((location) => (
                  <MenuItem key={location} value={location}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" />
                      {location}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Service Type Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#D4AF37' }}>Type *</InputLabel>
              <Select
                value={formData.type}
                label="Type *"
                onChange={(e) => handleInputChange('type', e.target.value)}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuItem value="installation">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Build fontSize="small" />
                    Installation
                  </Box>
                </MenuItem>
                <MenuItem value="service">
                  <Box display="flex" alignItems="center" gap={1}>
                    <BuildCircle fontSize="small" />
                    Service
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Time Slot Selection - Only show if type is installation */}
          {formData.type === 'installation' && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#D4AF37', mb: 2 }}>
                Available Time Slots
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {timeSlots.map((time) => (
                  <Chip
                    key={time}
                    label={formatTime(time)}
                    onClick={() => handleInputChange('timeSlot', time)}
                    color={formData.timeSlot === time ? 'primary' : 'default'}
                    sx={{
                      backgroundColor: formData.timeSlot === time ? '#D4AF37' : '#2C2C2C',
                      color: formData.timeSlot === time ? '#000000' : '#FFFFFF',
                      '&:hover': {
                        backgroundColor: formData.timeSlot === time ? '#B8941F' : '#3A3A3A',
                      }
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Available Technicians - Only show if time slot is selected */}
          {formData.timeSlot && availableTechs.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#D4AF37', mb: 2 }}>
                Available Technicians for {formatTime(formData.timeSlot)}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                {availableTechs.map((tech) => (
                  <Box
                    key={tech.id}
                    onClick={() => handleTechToggle(tech.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      border: selectedTechs.includes(tech.id) ? '2px solid #D4AF37' : '1px solid #333333',
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: selectedTechs.includes(tech.id) ? 'rgba(212, 175, 55, 0.1)' : '#2C2C2C',
                      '&:hover': {
                        backgroundColor: selectedTechs.includes(tech.id) ? 'rgba(212, 175, 55, 0.2)' : '#3A3A3A',
                      }
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, backgroundColor: '#D4AF37', color: '#000000' }}>
                      {tech.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                        {tech.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                        {tech.specialties.join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}

          {/* Customer Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#D4AF37' }}>Customer *</InputLabel>
              <Select
                value={selectedCustomer?._id || ''}
                label="Customer *"
                onChange={(e) => handleCustomerChange(e.target.value)}
                sx={{ color: '#FFFFFF' }}
              >
                {customers.map((customer) => (
                  <MenuItem key={customer._id} value={customer._id}>
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                        {customer.companyName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                        {customer.contactPerson.firstName} {customer.contactPerson.lastName} - {customer.status}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              sx={{
                '& .MuiInputLabel-root': { color: '#D4AF37' },
                '& .MuiOutlinedInput-root': { color: '#FFFFFF' }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#D4AF37' }}>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleInputChange('priority', e.target.value)}
                sx={{ color: '#FFFFFF' }}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Estimated Duration (hours)"
              type="number"
              value={formData.estimatedDuration}
              onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 4)}
              sx={{
                '& .MuiInputLabel-root': { color: '#D4AF37' },
                '& .MuiOutlinedInput-root': { color: '#FFFFFF' }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              sx={{
                '& .MuiInputLabel-root': { color: '#D4AF37' },
                '& .MuiOutlinedInput-root': { color: '#FFFFFF' }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333333' }}>
        <Button 
          onClick={onClose}
          sx={{ color: '#FFFFFF' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.location || !formData.type || !selectedCustomer || selectedTechs.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <Schedule />}
          sx={{ 
            backgroundColor: '#D4AF37', 
            color: '#000000',
            '&:hover': { backgroundColor: '#B8941F' },
            '&:disabled': { backgroundColor: '#666666' }
          }}
        >
          {loading ? 'Creating...' : 'Create Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleForm;
